<script lang="ts">
  import { untrack } from 'svelte'
  import { marked } from 'marked'
  import DOMPurify from 'dompurify'
  import { Editor, mergeAttributes, Node } from '@tiptap/core'
  import { Markdown } from '@tiptap/markdown'
  import StarterKit from '@tiptap/starter-kit'
  import { Table } from '@tiptap/extension-table'
  import TableCell from '@tiptap/extension-table-cell'
  import TableHeader from '@tiptap/extension-table-header'
  import TableRow from '@tiptap/extension-table-row'
  import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js'
  import 'monaco-editor/esm/vs/basic-languages/markdown/markdown.contribution.js'
  import 'monaco-editor/esm/vs/language/html/monaco.contribution.js'
  import 'monaco-editor/esm/vs/language/json/monaco.contribution.js'
  import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
  import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
  import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
  import { clearPersistedLicense, loadPersistedLicense, redeem } from './lib/license'
  import ReportView from './lib/schemas/ReportView.svelte'
  import RecordView from './lib/schemas/RecordView.svelte'
  import OutlineView from './lib/schemas/OutlineView.svelte'
  import { detectSchema, type SchemaKind } from './lib/schemas'

  type ViewMode = 'Reader' | 'Source' | 'Split'
  type DocType = 'MD' | 'HTML' | 'JSON'
  type FormatSelection = 'Auto' | DocType
  type PasteFormat = 'Auto' | DocType
  type ChipVariant = 'coral' | 'mint' | 'amber' | 'cyan' | 'violet'

  const viewModes: ViewMode[] = ['Source', 'Split', 'Reader']
  const docTypes: DocType[] = ['MD', 'HTML', 'JSON']
  const formatOptions: FormatSelection[] = ['Auto', ...docTypes]
  const chipVariants = new Set<ChipVariant>(['coral', 'mint', 'amber', 'cyan', 'violet'])
  const uglylessMonacoTheme = 'uglyless-dark'
  const monacoGlobal = self as typeof globalThis & {
    MonacoEnvironment?: monaco.Environment
  }

  monacoGlobal.MonacoEnvironment = {
    getWorker(_workerId: string, label: string) {
      if (label === 'json') {
        return new jsonWorker()
      }

      if (label === 'html') {
        return new htmlWorker()
      }

      return new editorWorker()
    },
  }

  const Chip = Node.create({
    name: 'chip',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: false,
    markdownTokenName: 'html',

    addAttributes() {
      return {
        label: {
          default: '',
          parseHTML: (element) => element.textContent || '',
          renderHTML: (attributes) => ({
            'data-label': typeof attributes.label === 'string' ? attributes.label : '',
          }),
        },
        variant: {
          default: 'coral',
          parseHTML: (element) => {
            const variant = Array.from(element.classList)
              .map((className) => className.replace('chip-', ''))
              .find((className): className is ChipVariant => chipVariants.has(className as ChipVariant))

            return variant ?? 'coral'
          },
        },
      }
    },

    parseHTML() {
      return [
        {
          tag: 'span.chip',
          getAttrs: (element) => {
            if (!(element instanceof HTMLElement)) {
              return false
            }

            return element.textContent == null ? false : null
          },
        },
      ]
    },

    renderHTML({ HTMLAttributes }) {
      const variant = chipVariants.has(HTMLAttributes.variant as ChipVariant)
        ? (HTMLAttributes.variant as ChipVariant)
        : 'coral'
      const label = typeof HTMLAttributes.label === 'string' ? HTMLAttributes.label : ''

      return [
        'span',
        mergeAttributes(HTMLAttributes, {
          class: `chip chip-${variant}`,
          contenteditable: 'false',
        }),
        label,
      ]
    },

    renderText({ node }) {
      const variant = node.attrs.variant === 'coral' ? '' : `-${node.attrs.variant as string}`
      return `[chip${variant}:${node.attrs.label as string}]`
    },

    renderMarkdown(node) {
      const variant = node.attrs?.variant === 'coral' ? '' : `-${node.attrs?.variant as string}`
      return `[chip${variant}:${node.attrs?.label as string}]`
    },
  })

  const tiptapExtensions = [
    StarterKit.configure({
      link: {
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noreferrer',
          target: '_blank',
        },
      },
    }),
    Table.configure({
      resizable: false,
    }),
    TableRow,
    TableHeader,
    TableCell,
    Chip,
    Markdown.configure({
      markedOptions: {
        breaks: true,
        gfm: true,
      },
    }),
  ]

  const sampleMarkdown = `# Hello, *Uglyless*

Ugly markdown should still feel like a crafted report when it reaches the reader.

## What changes

1. **Parse** [chip:MD] - Convert the source document into structured HTML.
2. **Clean** [chip-mint:SAFE] - Remove risky tags and attributes before display.
3. **Render** [chip-cyan:VIEW] - Apply the dark coral, mint, amber, and cyan treatment.

> The source stays plain. The reading experience does not have to. - Uglyless note

\`\`\`ts
const view = renderMarkdown(source)
if (view.safe) {
  showPrettyDocument(view.html)
}
\`\`\`

| Mode | Status | Accent |
|---|---|---|
| Uglyless | Ready | Coral |
| Ugly | Stub | Amber |
| Split | Stub | Cyan |`

  const sampleHtml = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <title>Uglyless HTML Sample</title>
  </head>
  <body>
    <h1>Service Report</h1>
    <p>The shop summary is already structured HTML, so Uglyless renders it directly.</p>
    <ul>
      <li>Checked intake notes</li>
      <li>Cleaned unsafe inline markup</li>
      <li>Preserved useful document structure</li>
    </ul>
    <pre><code>status = "ready"
next_step = "review"</code></pre>
  </body>
</html>`

  const sampleJsonTemplate = `{
  "report_id": "SR-2026-05-16-001",
  "created_at": "2026-05-16T14:32:00Z",
  "shop": {
    "name": "AutoWorks Service Center",
    "technician": "Jordan Lee",
    "technician_id": "TECH-1024"
  },
  "customer": {
    "name": "Alex Morgan",
    "email": "alex.morgan@example.com",
    "phone": "+1 (555) 123-4567",
    "verified": true
  },
  "vehicle": {
    "year": 2021,
    "make": "Toyota",
    "model": "RAV4",
    "trim": "XLE",
    "vin": "JTMWFREV3MJ123456",
    "mileage": 45231
  },
  "status": "Needs Attention",
  "priority": "High",
  "next_step": "Schedule Repairs",
  "findings": [
    {
      "id": "F-1",
      "category": "Brakes",
      "severity": "High",
      "title": "Front brake pads worn",
      "details": "Front brake pads are worn to 2mm. Recommended replacement within 500 miles.",
      "recommendation": "Replace front brake pads and inspect rotors.",
      "status": "Needs Attention"
    },
    {
      "id": "F-2",
      "category": "Tires",
      "severity": "Medium",
      "title": "Rear tires low on tread",
      "details": "Rear tire tread depth is 3/32\\\".",
      "recommendation": "Monitor and replace soon.",
      "status": "Monitor"
    },
    {
      "id": "F-3",
      "category": "Electrical",
      "severity": "Low",
      "title": "Battery health good",
      "details": "Battery tested at 92% CCA.",
      "recommendation": "No action needed.",
      "status": "Good"
    }
  ],
  "recommended_actions": [
    {
      "action": "Replace front brake pads and inspect rotors",
      "priority": "High"
    },
    {
      "action": "Rotate tires and recheck in 3,000 miles",
      "priority": "Medium"
    }
  ]
}`

  const allowedTags = new Set([
    'A',
    'BLOCKQUOTE',
    'BR',
    'CODE',
    'EM',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'HR',
    'LI',
    'OL',
    'P',
    'PRE',
    'SPAN',
    'STRONG',
    'TABLE',
    'TBODY',
    'TD',
    'TH',
    'THEAD',
    'TR',
    'UL',
  ])

  const allowedAttributes = new Map<string, Set<string>>([
    ['A', new Set(['href', 'title'])],
    ['CODE', new Set(['class'])],
    ['SPAN', new Set(['class'])],
    ['TD', new Set(['align'])],
    ['TH', new Set(['align'])],
  ])

  const blockedUrlPattern = /^(?:javascript|data|vbscript):/i
  const chipPattern = /\[chip(?:-(coral|mint|amber|cyan|violet))?:([^\]]+)\]/g
  const viewModeStorageKey = 'uglyless-pro-view-mode'
  const docTypeStorageKey = 'uglyless-pro-doc-type'
  const htmlVoidTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ])

  let isUglylessMonacoThemeDefined = false

  function escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }

  function applyUglylessInlineTokens(markdown: string): string {
    return markdown.replace(chipPattern, (_, variant: string | undefined, label: string) => {
      const chipVariant = variant ?? 'coral'
      return `<span class="chip chip-${chipVariant}">${escapeHtml(label)}</span>`
    })
  }

  function restrictRenderedHtml(markup: string): string {
    const template = document.createElement('template')
    template.innerHTML = markup

    for (const element of Array.from(template.content.querySelectorAll('*'))) {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(document.createTextNode(element.textContent ?? ''))
        continue
      }

      const tagAttributes = allowedAttributes.get(element.tagName) ?? new Set<string>()

      for (const attribute of Array.from(element.attributes)) {
        const attributeName = attribute.name.toLowerCase()
        const isAllowedAttribute = tagAttributes.has(attributeName)
        const isSafeHref =
          attributeName !== 'href' || !blockedUrlPattern.test(attribute.value.trim())
        const isSafeClass =
          attributeName !== 'class' ||
          /^(?:language-[a-z0-9-]+|chip(?: chip-(?:coral|mint|amber|cyan|violet))?)$/i.test(
            attribute.value,
          )
        const isSafeAlign =
          attributeName !== 'align' || /^(?:left|center|right)$/i.test(attribute.value)

        if (!isAllowedAttribute || !isSafeHref || !isSafeClass || !isSafeAlign) {
          element.removeAttribute(attribute.name)
        }
      }

      if (element.tagName === 'A') {
        element.setAttribute('rel', 'noreferrer')
        element.setAttribute('target', '_blank')
      }
    }

    return template.innerHTML
  }

  function sanitizeRenderedHtml(markup: string): string {
    return DOMPurify.sanitize(restrictRenderedHtml(markup))
  }

  function renderHtmlDocument(source: string): string {
    const parser = new DOMParser()
    const parsedDocument = parser.parseFromString(source, 'text/html')
    const bodyMarkup = parsedDocument.body.innerHTML || source

    return sanitizeRenderedHtml(bodyMarkup)
  }

  function detectDocType(source: string): DocType {
    const trimmedSource = source.trimStart()

    if (trimmedSource.startsWith('{') || trimmedSource.startsWith('[')) {
      return 'JSON'
    }

    if (trimmedSource.startsWith('<') || /<html[\s>]/i.test(trimmedSource)) {
      return 'HTML'
    }

    return 'MD'
  }

  function isJsonValid(source: string): boolean {
    try {
      JSON.parse(source)
      return true
    } catch {
      return false
    }
  }

  function isHtmlBasicallyWellFormed(source: string): boolean {
    const tagPattern = /<\/?([a-z][a-z0-9-]*)(?:\s[^>]*)?>/gi
    const openTags: string[] = []
    let match: RegExpExecArray | null

    while ((match = tagPattern.exec(source)) !== null) {
      const rawTag = match[0]
      const tagName = match[1].toLowerCase()

      if (rawTag.startsWith('</')) {
        if (openTags.pop() !== tagName) {
          return false
        }
        continue
      }

      if (htmlVoidTags.has(tagName) || rawTag.endsWith('/>') || rawTag.startsWith('<!')) {
        continue
      }

      openTags.push(tagName)
    }

    return openTags.length === 0
  }

  function getValidityLabel(docType: DocType, source: string): string {
    if (docType === 'JSON') {
      return isJsonValid(source) ? 'JSON is valid' : 'JSON is invalid'
    }

    if (docType === 'HTML') {
      return isHtmlBasicallyWellFormed(source) ? 'HTML is valid' : 'HTML needs review'
    }

    return 'Markdown is valid'
  }

  function getReaderText(): string {
    if (selectedDocType === 'JSON') {
      return sampleDoc
    }

    const template = document.createElement('template')
    template.innerHTML = renderedSampleDoc
    return (template.content.textContent ?? '').trim()
  }

  function isViewMode(value: string | null): value is ViewMode {
    return value === 'Reader' || value === 'Source' || value === 'Split'
  }

  function loadInitialViewMode(): ViewMode {
    try {
      const persistedMode = window.localStorage.getItem(viewModeStorageKey)
      return isViewMode(persistedMode) ? persistedMode : 'Split'
    } catch {
      return 'Split'
    }
  }

  function isDocType(value: string | null): value is DocType {
    return value === 'MD' || value === 'HTML' || value === 'JSON'
  }

  function loadInitialDocType(): DocType {
    try {
      const persistedDocType = window.localStorage.getItem(docTypeStorageKey)
      return isDocType(persistedDocType) ? persistedDocType : 'JSON'
    } catch {
      return 'JSON'
    }
  }

  function getJsonSchemaKind(source: string): SchemaKind {
    try {
      return detectSchema(JSON.parse(source), source).kind
    } catch {
      return 'outline'
    }
  }

  function defineUglylessMonacoTheme(): void {
    if (isUglylessMonacoThemeDefined) {
      return
    }

    monaco.editor.defineTheme(uglylessMonacoTheme, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '9a948b' },
        { token: 'keyword', foreground: 'ff7a4d' },
        { token: 'string', foreground: '5fd7a4' },
        { token: 'number', foreground: 'f0b454' },
        { token: 'tag', foreground: 'ff7a4d' },
      ],
      colors: {
        'editor.background': '#0a0a0b',
        'editor.foreground': '#f5efe6',
        'editorCursor.foreground': '#ff7a4d',
        'editorLineNumber.foreground': '#5e5a55',
        'editorLineNumber.activeForeground': '#ff7a4d',
        'editor.selectionBackground': '#ff7a4d33',
        'editor.inactiveSelectionBackground': '#ff7a4d1f',
        'editorIndentGuide.background1': '#2a2a2e',
        'editorIndentGuide.activeBackground1': '#ff7a4d',
        'editorWidget.background': '#141414',
        'focusBorder': '#ff7a4d',
      },
    })

    isUglylessMonacoThemeDefined = true
  }

  let selectedMode = $state<ViewMode>(loadInitialViewMode())
  let viewportIsWide = $state(typeof window !== 'undefined' && window.innerWidth > 1024)

  $effect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => {
      viewportIsWide = window.innerWidth > 1024
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  })

  // Auto-leave Split when viewport narrows below side-by-side threshold
  $effect(() => {
    if (!viewportIsWide && selectedMode === 'Split') {
      untrack(() => {
        selectedMode = 'Reader'
      })
    }
  })
  let selectedDocType = $state<DocType>(loadInitialDocType())
  let selectedFormat = $state<FormatSelection>('Auto')
  let forceJsonTree = $state(false)
  let hasProLicense = $state(loadPersistedLicense())
  let isLicenseModalOpen = $state(false)
  let isPasteModalOpen = $state(false)
  let licenseKeyInput = $state('')
  let licenseError = $state('')
  let licenseInputElement = $state<HTMLInputElement>()
  let pasteSourceInput = $state('')
  let pasteFormat = $state<PasteFormat>('Auto')
  let pasteTextAreaElement = $state<HTMLTextAreaElement>()
  let fileInputElement = $state<HTMLInputElement>()
  let isProMenuOpen = $state(false)
  let sampleMd = $state(sampleMarkdown)
  let sampleHtmlSource = $state(sampleHtml)
  let sampleJson = $state(sampleJsonTemplate)
  let sourceCursorLine = $state(1)
  let sourceCursorColumn = $state(1)
  let toastMessage = $state('')
  let toastTimer = 0
  let uglyEditorElement = $state<HTMLDivElement>()
  let uglyEditor = $state<monaco.editor.IStandaloneCodeEditor>()
  let uglylessEditorElement = $state<HTMLElement>()
  let uglylessTiptapEditor = $state<Editor>()
  let splitUglyEditorElement = $state<HTMLDivElement>()
  let splitUglylessPaneElement = $state<HTMLElement>()
  let splitUglylessEditorElement = $state<HTMLElement>()
  let splitUglyEditor = $state<monaco.editor.IStandaloneCodeEditor>()
  let splitUglylessTiptapEditor = $state<Editor>()
  let splitLeftPercent = $state(50)
  let activeEditorContent = $state('')
  let hasUnsavedChanges = $state(false)
  let isSaving = $state(false)
  let savedAt = $state(Date.now())
  let statusNow = $state(Date.now())
  let saveStatusTimer = 0

  const sampleDoc = $derived(
    selectedDocType === 'MD'
      ? sampleMd
      : selectedDocType === 'HTML'
        ? sampleHtmlSource
        : sampleJson,
  )
  const renderedSampleDoc = $derived(
    selectedDocType === 'HTML'
      ? renderHtmlDocument(sampleHtmlSource)
      : sanitizeRenderedHtml(
          marked.parse(applyUglylessInlineTokens(sampleMd), {
            async: false,
            breaks: true,
            gfm: true,
          }) as string,
        ),
  )
  const sampleDocLanguage = $derived(
    selectedDocType === 'MD' ? 'markdown' : selectedDocType.toLowerCase(),
  )
  const splitPaneStyle = $derived(
    `grid-template-columns: minmax(0, ${splitLeftPercent}%) 10px minmax(0, ${
      100 - splitLeftPercent
    }%)`,
  )
  const savedSecondsAgo = $derived(Math.max(0, Math.floor((statusNow - savedAt) / 1000)))
  const saveStatusLabel = $derived(
    isSaving
      ? 'Saving...'
      : hasUnsavedChanges
        ? 'Unsaved'
        : `Saved ${savedSecondsAgo}s ago`,
  )
  const validityLabel = $derived(getValidityLabel(selectedDocType, sampleDoc))
  const syncStateLabel = $derived(hasUnsavedChanges ? 'Unsaved' : 'Synced')
  const editStateLabel = $derived(hasProLicense ? 'Editing enabled' : 'Read-only')
  const languageLabel = $derived(selectedDocType)
  const jsonSchemaKind = $derived(selectedDocType === 'JSON' ? getJsonSchemaKind(sampleDoc) : 'outline')

  function updateSampleDoc(value: string): void {
    if (selectedDocType === 'MD') {
      sampleMd = value
      return
    }

    if (selectedDocType === 'HTML') {
      sampleHtmlSource = value
      return
    }

    sampleJson = value
  }

  function setDocTypeFromSource(value: string, requestedFormat: PasteFormat = 'Auto'): void {
    const nextDocType = requestedFormat === 'Auto' ? detectDocType(value) : requestedFormat
    selectedDocType = nextDocType
    selectedFormat = requestedFormat === 'Auto' ? 'Auto' : nextDocType

    if (nextDocType === 'MD') {
      sampleMd = value
    } else if (nextDocType === 'HTML') {
      sampleHtmlSource = value
    } else {
      sampleJson = value
    }

    activeEditorContent = value
    hasUnsavedChanges = false
    savedAt = Date.now()
    statusNow = savedAt
  }

  function handleFormatSelection(format: FormatSelection): void {
    selectedFormat = format
    selectedDocType = format === 'Auto' ? detectDocType(sampleDoc) : format
  }

  function openFilePicker(): void {
    fileInputElement?.click()
  }

  async function handleFileInputChange(event: Event): Promise<void> {
    const input = event.currentTarget

    if (!(input instanceof HTMLInputElement) || !input.files?.[0]) {
      return
    }

    const file = input.files[0]
    const extension = file.name.split('.').pop()?.toLowerCase()
    const source = await file.text()
    const detectedFormat: DocType =
      extension === 'json' ? 'JSON' : extension === 'html' || extension === 'htm' ? 'HTML' : 'MD'

    setDocTypeFromSource(source, detectedFormat)
    input.value = ''
  }

  function openPasteModal(): void {
    pasteSourceInput = ''
    pasteFormat = 'Auto'
    isPasteModalOpen = true
  }

  function closePasteModal(): void {
    isPasteModalOpen = false
    pasteSourceInput = ''
    pasteFormat = 'Auto'
  }

  function submitPasteSource(): void {
    setDocTypeFromSource(pasteSourceInput, pasteFormat)
    closePasteModal()
  }

  function showToast(message: string): void {
    toastMessage = message
    window.clearTimeout(toastTimer)
    toastTimer = window.setTimeout(() => {
      toastMessage = ''
    }, 1400)
  }

  async function copyText(value: string): Promise<void> {
    await navigator.clipboard.writeText(value)
    showToast('Copied')
  }

  function updateSourceCursor(editor: monaco.editor.IStandaloneCodeEditor): void {
    const position = editor.getPosition()
    sourceCursorLine = position?.lineNumber ?? 1
    sourceCursorColumn = position?.column ?? 1
  }

  function createSourceEditor(
    container: HTMLDivElement,
    options: { editableJson: boolean },
  ): monaco.editor.IStandaloneCodeEditor {
    defineUglylessMonacoTheme()
    const initialSampleDoc = untrack(() => sampleDoc)
    const initialSampleDocLanguage = untrack(() => sampleDocLanguage)
    const initialDocType = untrack(() => selectedDocType)

    return monaco.editor.create(container, {
      value: initialSampleDoc,
      language: initialSampleDocLanguage,
      theme: uglylessMonacoTheme,
      readOnly: initialDocType !== 'JSON' || !options.editableJson,
      minimap: { enabled: false },
      lineNumbers: 'on',
      wordWrap: 'on',
      folding: true,
      scrollBeyondLastLine: false,
      automaticLayout: true,
      fontSize: 14,
      lineHeight: 22,
      padding: { top: 16, bottom: 16 },
      renderLineHighlight: 'line',
    })
  }

  function createUglylessEditor(container: HTMLElement, docType: Exclude<DocType, 'JSON'>): Editor {
    const initialSampleDoc = untrack(() => sampleDoc)

    return new Editor({
      element: container,
      extensions: tiptapExtensions,
      content: applyUglylessInlineTokens(initialSampleDoc),
      contentType: docType === 'HTML' ? 'html' : 'markdown',
      editorProps: {
        attributes: {
          class: 'uglyless-prose',
          spellcheck: 'true',
        },
      },
      onUpdate({ editor }) {
        activeEditorContent =
          docType === 'HTML'
            ? `${editor.getHTML().trimEnd()}\n`
            : `${editor.getMarkdown().trimEnd()}\n`
        hasUnsavedChanges = activeEditorContent !== sampleDoc
      },
    })
  }

  function openLicenseModal(): void {
    licenseKeyInput = ''
    licenseError = ''
    isLicenseModalOpen = true
    isProMenuOpen = false
  }

  function closeLicenseModal(): void {
    isLicenseModalOpen = false
    licenseError = ''
  }

  function submitLicense(): void {
    if (!redeem(licenseKeyInput)) {
      licenseError = "That key doesn't look valid"
      return
    }

    hasProLicense = true
    licenseKeyInput = ''
    licenseError = ''
    isLicenseModalOpen = false
  }

  function signOutOfPro(): void {
    clearPersistedLicense()
    hasProLicense = false
    isProMenuOpen = false
  }

  function handleModalBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      closeLicenseModal()
    }
  }

  function handleWindowKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return
    }

    if (isLicenseModalOpen) {
      closeLicenseModal()
      return
    }

    if (isPasteModalOpen) {
      closePasteModal()
      return
    }

    isProMenuOpen = false
  }

  function clampSplitPercent(value: number): number {
    return Math.min(68, Math.max(32, value))
  }

  function startSplitResize(event: PointerEvent): void {
    const splitContainer = event.currentTarget instanceof HTMLElement
      ? event.currentTarget.closest<HTMLElement>('.split-view')
      : null

    if (!splitContainer) {
      return
    }

    event.preventDefault()

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const bounds = splitContainer.getBoundingClientRect()
      const nextPercent = ((moveEvent.clientX - bounds.left) / bounds.width) * 100
      splitLeftPercent = clampSplitPercent(nextPercent)
    }

    const handlePointerUp = (): void => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      splitUglyEditor?.layout()
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  function adjustSplitWithKeyboard(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return
    }

    event.preventDefault()
    splitLeftPercent = clampSplitPercent(
      splitLeftPercent + (event.key === 'ArrowRight' ? 4 : -4),
    )
    splitUglyEditor?.layout()
  }

  function syncSourceEditor(editor: monaco.editor.IStandaloneCodeEditor): void {
    if (editor.getValue() !== sampleDoc) {
      editor.setValue(sampleDoc)
    }

    const model = editor.getModel()

    if (model && model.getLanguageId() !== sampleDocLanguage) {
      monaco.editor.setModelLanguage(model, sampleDocLanguage)
    }

    editor.updateOptions({
      readOnly: selectedDocType !== 'JSON' || !hasProLicense,
    })
    updateSourceCursor(editor)
  }

  function bindJsonEditing(editor: monaco.editor.IStandaloneCodeEditor): monaco.IDisposable {
    return editor.onDidChangeModelContent(() => {
      if (selectedDocType !== 'JSON' || !hasProLicense) {
        return
      }

      const value = editor.getValue()
      activeEditorContent = value
      hasUnsavedChanges = value !== sampleJson
    })
  }

  function getActiveEditorContent(): string {
    if (selectedMode === 'Source') {
      return uglyEditor?.getValue() ?? activeEditorContent
    }

    if (selectedMode === 'Reader') {
      if (selectedDocType === 'JSON') {
        return sampleDoc
      }

      if (selectedDocType === 'HTML') {
        return `${uglylessTiptapEditor?.getHTML().trimEnd() ?? activeEditorContent.trimEnd()}\n`
      }

      return `${uglylessTiptapEditor?.getMarkdown().trimEnd() ?? activeEditorContent.trimEnd()}\n`
    }

    if (selectedDocType === 'JSON') {
      return splitUglyEditor?.getValue() ?? activeEditorContent
    }

    if (selectedDocType === 'HTML') {
      return `${splitUglylessTiptapEditor?.getHTML().trimEnd() ?? activeEditorContent.trimEnd()}\n`
    }

    return `${splitUglylessTiptapEditor?.getMarkdown().trimEnd() ?? activeEditorContent.trimEnd()}\n`
  }

  function saveActiveDoc(): void {
    if (isSaving || !hasUnsavedChanges) {
      return
    }

    isSaving = true
    const nextSampleDoc = getActiveEditorContent()
    updateSampleDoc(nextSampleDoc)
    activeEditorContent = nextSampleDoc
    hasUnsavedChanges = false
    savedAt = Date.now()
    statusNow = savedAt

    window.setTimeout(() => {
      isSaving = false
    }, 450)
  }

  $effect(() => {
    if (selectedMode !== 'Source' || !uglyEditorElement) {
      return
    }

    const currentDocType = selectedDocType
    const editor = createSourceEditor(uglyEditorElement, {
      editableJson: currentDocType === 'JSON' && hasProLicense,
    })
    const jsonEditingDisposable = bindJsonEditing(editor)
    const cursorDisposable = editor.onDidChangeCursorPosition(() => {
      updateSourceCursor(editor)
    })
    untrack(() => uglyEditor)?.dispose()
    uglyEditor = editor
    updateSourceCursor(editor)

    return () => {
      cursorDisposable.dispose()
      jsonEditingDisposable.dispose()
      editor.dispose()

      if (uglyEditor === editor) {
        uglyEditor = undefined
      }
    }
  })

  $effect(() => {
    uglyEditor && syncSourceEditor(uglyEditor)
    splitUglyEditor && syncSourceEditor(splitUglyEditor)
  })

  $effect(() => {
    if (
      selectedMode !== 'Reader' ||
      selectedDocType === 'JSON' ||
      !hasProLicense ||
      !uglylessEditorElement
    ) {
      return
    }

    const currentDocType = selectedDocType
    const editor = createUglylessEditor(uglylessEditorElement, currentDocType)
    untrack(() => uglylessTiptapEditor)?.destroy()
    uglylessTiptapEditor = editor

    return () => {
      editor.destroy()

      if (uglylessTiptapEditor === editor) {
        uglylessTiptapEditor = undefined
      }
    }
  })

  $effect(() => {
    if (
      selectedMode !== 'Split' ||
      selectedDocType === 'JSON' ||
      !hasProLicense ||
      !splitUglylessEditorElement
    ) {
      return
    }

    const currentDocType = selectedDocType
    const editor = createUglylessEditor(splitUglylessEditorElement, currentDocType)
    untrack(() => splitUglylessTiptapEditor)?.destroy()
    splitUglylessTiptapEditor = editor

    return () => {
      editor.destroy()

      if (splitUglylessTiptapEditor === editor) {
        splitUglylessTiptapEditor = undefined
      }
    }
  })

  $effect(() => {
    if (selectedMode !== 'Split' || !splitUglyEditorElement || !splitUglylessPaneElement) {
      return
    }

    const currentDocType = selectedDocType
    const editor = createSourceEditor(splitUglyEditorElement, {
      editableJson: currentDocType === 'JSON' && hasProLicense,
    })
    const jsonEditingDisposable = bindJsonEditing(editor)
    const uglylessPane = splitUglylessPaneElement
    let isSyncingScroll = false
    untrack(() => splitUglyEditor)?.dispose()
    splitUglyEditor = editor

    const releaseScrollSync = (): void => {
      requestAnimationFrame(() => {
        isSyncingScroll = false
      })
    }

    const syncFromUglyToUglyless = (): void => {
      if (isSyncingScroll) {
        return
      }

      const uglyScrollableHeight = Math.max(0, editor.getScrollHeight() - editor.getLayoutInfo().height)
      const uglylessScrollableHeight = Math.max(
        0,
        uglylessPane.scrollHeight - uglylessPane.clientHeight,
      )
      const scrollPercent = uglyScrollableHeight === 0 ? 0 : editor.getScrollTop() / uglyScrollableHeight

      isSyncingScroll = true
      uglylessPane.scrollTop = scrollPercent * uglylessScrollableHeight
      releaseScrollSync()
    }

    const syncFromUglylessToUgly = (): void => {
      if (isSyncingScroll) {
        return
      }

      const uglylessScrollableHeight = Math.max(
        0,
        uglylessPane.scrollHeight - uglylessPane.clientHeight,
      )
      const uglyScrollableHeight = Math.max(0, editor.getScrollHeight() - editor.getLayoutInfo().height)
      const scrollPercent =
        uglylessScrollableHeight === 0 ? 0 : uglylessPane.scrollTop / uglylessScrollableHeight

      isSyncingScroll = true
      editor.setScrollTop(scrollPercent * uglyScrollableHeight)
      releaseScrollSync()
    }

    const editorScrollDisposable = editor.onDidScrollChange(syncFromUglyToUglyless)
    uglylessPane.addEventListener('scroll', syncFromUglylessToUgly, { passive: true })

    return () => {
      editorScrollDisposable.dispose()
      jsonEditingDisposable.dispose()
      uglylessPane.removeEventListener('scroll', syncFromUglylessToUgly)
      editor.dispose()

      if (splitUglyEditor === editor) {
        splitUglyEditor = undefined
      }
    }
  })

  $effect(() => {
    window.addEventListener('keydown', handleWindowKeydown)

    return () => {
      window.removeEventListener('keydown', handleWindowKeydown)
    }
  })

  $effect(() => {
    if (!isLicenseModalOpen || !licenseInputElement) {
      return
    }

    requestAnimationFrame(() => {
      licenseInputElement?.focus()
    })
  })

  $effect(() => {
    if (!isPasteModalOpen || !pasteTextAreaElement) {
      return
    }

    requestAnimationFrame(() => {
      pasteTextAreaElement?.focus()
    })
  })

  $effect(() => {
    try {
      window.localStorage.setItem(viewModeStorageKey, selectedMode)
    } catch {
      return
    }
  })

  $effect(() => {
    try {
      window.localStorage.setItem(docTypeStorageKey, selectedDocType)
    } catch {
      return
    }
  })

  $effect(() => {
    selectedDocType
    selectedMode
    activeEditorContent = sampleDoc
    hasUnsavedChanges = false
    forceJsonTree = false
  })

  $effect(() => {
    saveStatusTimer = window.setInterval(() => {
      statusNow = Date.now()
    }, 10000)

    return () => {
      window.clearInterval(saveStatusTimer)
    }
  })
</script>

<main class="app-shell">
  <header class="site-header">
    <div class="brand-lockup">
      <span class="u-badge" aria-hidden="true">U</span>
      <h1>Uglyless Pro</h1>
      <p>Turn raw documents into readable output</p>
    </div>
    <div class="header-actions">
      <input
        bind:this={fileInputElement}
        type="file"
        class="hidden-file-input"
        accept=".md,.markdown,.html,.htm,.json,text/markdown,text/html,application/json,text/plain"
        onchange={handleFileInputChange}
      />
      <button type="button" class="ghost-button app-action" onclick={openFilePicker}>Open file</button>
      <button type="button" class="ghost-button app-action" onclick={openPasteModal}>Paste source</button>
      {#if hasProLicense}
        <div class="pro-menu">
          <button
            type="button"
            class="pro-chip"
            aria-haspopup="menu"
            aria-expanded={isProMenuOpen}
            onclick={() => {
              isProMenuOpen = !isProMenuOpen
            }}
          >
            Pro
          </button>
          {#if isProMenuOpen}
            <div class="pro-dropdown" role="menu">
              <button type="button" role="menuitem" onclick={signOutOfPro}>Sign out</button>
            </div>
          {/if}
        </div>
      {:else}
        <div class="pro-lock-overlay header-pro" aria-label="Pro editing locked">
          <button type="button" onclick={openLicenseModal}>EDIT</button>
          <span>Pro</span>
        </div>
      {/if}
    </div>
  </header>

  <section class="mode-panel" aria-label="Document controls">
    <div>
      <h2 id="view-mode-heading">View mode</h2>
      <div class="mode-toggle" role="group" aria-labelledby="view-mode-heading">
        <button
          type="button"
          class:selected={selectedMode === 'Source'}
          aria-pressed={selectedMode === 'Source'}
          onclick={() => {
            selectedMode = 'Source'
          }}
        >
          Source
        </button>
        {#if viewportIsWide}
          <button
            type="button"
            class:selected={selectedMode === 'Split'}
            aria-pressed={selectedMode === 'Split'}
            onclick={() => {
              selectedMode = 'Split'
            }}
          >
            Split
          </button>
        {:else}
          <button
            type="button"
            aria-label="Swap between Source and Reader"
            onclick={() => {
              selectedMode = selectedMode === 'Source' ? 'Reader' : 'Source'
            }}
          >
            Swap
          </button>
        {/if}
        <button
          type="button"
          class:selected={selectedMode === 'Reader'}
          aria-pressed={selectedMode === 'Reader'}
          onclick={() => {
            selectedMode = 'Reader'
          }}
        >
          Reader
        </button>
      </div>
    </div>
    <div>
      <h2 id="doc-type-heading">Format</h2>
      <div class="mode-toggle doc-type-toggle" role="group" aria-labelledby="doc-type-heading">
        {#each formatOptions as format}
          <button
            type="button"
            class:selected={selectedFormat === format}
            aria-pressed={selectedFormat === format}
            onclick={() => {
              handleFormatSelection(format)
            }}
          >
            {format === 'Auto' ? 'Format: Auto' : format}
          </button>
        {/each}
      </div>
    </div>
  </section>

  <section class="content-stage" data-sample-doc-loaded={sampleDoc.length > 0}>
    {#if hasProLicense || hasUnsavedChanges || isSaving}
      <div class="doc-save-overlay" aria-live="polite">
        <span class:unsaved={hasUnsavedChanges} class:saving={isSaving}>
          {#if isSaving}
            <span class="save-spinner" aria-hidden="true"></span>
          {/if}
          {saveStatusLabel}
        </span>
        <button type="button" disabled={!hasUnsavedChanges || isSaving} onclick={saveActiveDoc}>Save</button>
      </div>
    {/if}

    {#if selectedMode === 'Reader'}
      <div class="pro-doc-shell">
        <div class="pane-toolbar">
          <span>READER ({selectedDocType})</span>
          <div class="pane-toolbar-actions">
            {#if selectedDocType === 'JSON'}
              <button
                type="button"
                onclick={() => {
                  forceJsonTree = !forceJsonTree
                }}
              >
                {forceJsonTree ? 'Report View' : 'Switch to Tree'}
              </button>
            {/if}
            <button type="button" onclick={() => copyText(getReaderText())}>Copy report</button>
          </div>
        </div>
        {#if selectedDocType === 'JSON'}
          <article class="uglyless-rendered uglyless-json-outline" aria-label="JSON document">
            {#if forceJsonTree}
              <OutlineView value={sampleDoc} />
            {:else if jsonSchemaKind === 'report'}
              <ReportView value={sampleDoc} />
            {:else if jsonSchemaKind === 'record'}
              <RecordView value={sampleDoc} />
            {:else}
              <OutlineView value={sampleDoc} />
            {/if}
          </article>
        {:else if hasProLicense}
          <article class="uglyless-rendered uglyless-editable" bind:this={uglylessEditorElement}></article>
        {:else}
          <article class="uglyless-rendered">
            {@html renderedSampleDoc}
          </article>
          <div class="pro-lock-overlay" aria-label="Pro editing locked">
            <button type="button" onclick={openLicenseModal}>EDIT File</button>
            <span>Pro</span>
          </div>
        {/if}
      </div>
    {:else if selectedMode === 'Source'}
      <div class="pro-doc-shell">
        <div class="pane-toolbar">
          <span>SOURCE ({selectedDocType})</span>
          <button type="button" onclick={() => copyText(sampleDoc)}>Copy source</button>
        </div>
        <div class="ugly-editor-shell" aria-label="Raw source document">
          <div class="ugly-editor" bind:this={uglyEditorElement}></div>
        </div>
        {#if selectedDocType === 'JSON' && !hasProLicense}
          <div class="pro-lock-overlay" aria-label="Pro editing locked">
            <button type="button" onclick={openLicenseModal}>EDIT File</button>
            <span>Pro</span>
          </div>
        {/if}
      </div>
    {:else}
      <div class="split-view" style={splitPaneStyle}>
        <div class="split-pane split-pane-ugly" aria-label="Raw source document">
          <div class="pane-toolbar">
            <span>SOURCE ({selectedDocType})</span>
            <button type="button" onclick={() => copyText(sampleDoc)}>Copy source</button>
          </div>
          <div class="ugly-editor split-ugly-editor" bind:this={splitUglyEditorElement}></div>
        </div>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div
          role="separator"
          tabindex="0"
          class="split-divider"
          aria-label="Resize split panes"
          aria-orientation="vertical"
          aria-valuemin="32"
          aria-valuemax="68"
          aria-valuenow={Math.round(splitLeftPercent)}
          onpointerdown={startSplitResize}
          onkeydown={adjustSplitWithKeyboard}
        ></div>
        {#if selectedDocType === 'JSON'}
          <div
            class="split-pane split-pane-uglyless split-pane-json uglyless-rendered"
            aria-label="JSON document"
            bind:this={splitUglylessPaneElement}
          >
            <div class="pro-doc-shell">
              <div class="pane-toolbar">
                <span>READER ({selectedDocType})</span>
                <div class="pane-toolbar-actions">
                  <button
                    type="button"
                    onclick={() => {
                      forceJsonTree = !forceJsonTree
                    }}
                  >
                    {forceJsonTree ? 'Report View' : 'Switch to Tree'}
                  </button>
                  <button type="button" onclick={() => copyText(getReaderText())}>Copy report</button>
                </div>
              </div>
              <div class="reader-body uglyless-json-outline">
                {#if forceJsonTree}
                  <OutlineView value={sampleDoc} />
                {:else if jsonSchemaKind === 'report'}
                  <ReportView value={sampleDoc} />
                {:else if jsonSchemaKind === 'record'}
                  <RecordView value={sampleDoc} />
                {:else}
                  <OutlineView value={sampleDoc} />
                {/if}
              </div>
            </div>
          </div>
        {:else}
          <article
            class="split-pane split-pane-uglyless uglyless-rendered"
            aria-label="Rendered document"
            bind:this={splitUglylessPaneElement}
          >
            <div class="pro-doc-shell">
              <div class="pane-toolbar">
                <span>READER ({selectedDocType})</span>
                <button type="button" onclick={() => copyText(getReaderText())}>Copy report</button>
              </div>
              {#if hasProLicense}
                <div class="uglyless-editable" bind:this={splitUglylessEditorElement}></div>
              {:else}
                <div class="reader-body">
                  {@html renderedSampleDoc}
                </div>
                <div class="pro-lock-overlay" aria-label="Pro editing locked">
                  <span>Pro</span>
                  <button type="button" onclick={openLicenseModal}>EDIT File</button>
                </div>
              {/if}
            </div>
          </article>
        {/if}
      </div>
    {/if}
  </section>

  <footer class="status-footer" aria-label="Document status">
    <span class:status-invalid={validityLabel.includes('invalid') || validityLabel.includes('review')}>
      <span class="validity-marker" aria-hidden="true">*</span>
      {validityLabel}
    </span>
    {#if selectedMode === 'Source'}
      <span>Ln {sourceCursorLine}, Col {sourceCursorColumn}</span>
    {/if}
    <span>Spaces: 2</span>
    <span>UTF-8</span>
    <span>{languageLabel}</span>
    <span class:status-unsaved={hasUnsavedChanges}>{syncStateLabel}</span>
    <span>{editStateLabel}</span>
  </footer>

  {#if toastMessage}
    <div class="toast" role="status">{toastMessage}</div>
  {/if}

  {#if isPasteModalOpen}
    <div
      class="license-backdrop"
      role="presentation"
      onclick={(event) => {
        if (event.target === event.currentTarget) {
          closePasteModal()
        }
      }}
      onkeydown={handleWindowKeydown}
    >
      <div class="license-modal paste-modal" aria-labelledby="paste-modal-title" aria-modal="true" role="dialog">
        <form
          onsubmit={(event) => {
            event.preventDefault()
            submitPasteSource()
          }}
        >
          <h2 id="paste-modal-title">Paste source</h2>
          <label>
            <span>Format</span>
            <select bind:value={pasteFormat}>
              <option value="Auto">Auto detect</option>
              <option value="MD">Markdown</option>
              <option value="HTML">HTML</option>
              <option value="JSON">JSON</option>
            </select>
          </label>
          <label>
            <span>Source</span>
            <textarea
              bind:this={pasteTextAreaElement}
              bind:value={pasteSourceInput}
              placeholder="Paste raw Markdown, HTML, or JSON"
              rows="9"
              spellcheck="false"
            ></textarea>
          </label>
          <div class="license-actions">
            <button type="button" class="ghost-button" onclick={closePasteModal}>Cancel</button>
            <button type="submit" class="primary-button" disabled={pasteSourceInput.trim().length === 0}>Load source</button>
          </div>
        </form>
      </div>
    </div>
  {/if}

  {#if isLicenseModalOpen}
    <div
      class="license-backdrop"
      role="presentation"
      onclick={handleModalBackdropClick}
      onkeydown={handleWindowKeydown}
    >
      <div class="license-modal" aria-labelledby="license-modal-title" aria-modal="true" role="dialog">
        <form
          onsubmit={(event) => {
            event.preventDefault()
            submitLicense()
          }}
        >
          <h2 id="license-modal-title">Unlock Uglyless Pro</h2>
          <p>
            Reader, Source, and Split view are free. Pro unlocks live editing, round-trip source
            sync, and local license persistence.
          </p>
          <label>
            <span>License key</span>
            <input
              bind:this={licenseInputElement}
              bind:value={licenseKeyInput}
              placeholder="Paste your key"
              autocomplete="off"
              spellcheck="false"
            />
          </label>
          {#if licenseError}
            <p class="license-error">{licenseError}</p>
          {/if}
          <div class="license-actions">
            <button type="submit" class="primary-button">Unlock Pro</button>
            <button type="button" class="ghost-button" onclick={closeLicenseModal}>Continue read-only</button>
          </div>
          <p class="license-secondary">Your key is stored locally on this device. No account required.</p>
        </form>
      </div>
    </div>
  {/if}
</main>
